'use client';

/**
 * JotFormScript — client leaf for JotForm `jsform` script-loaders.
 *
 * Source HTML looks like:
 *   <script type="text/javascript" src="https://form.jotform.com/jsform/<id>"></script>
 *
 * JotForm's `jsform` loader appends an iframe (modern) or form markup (legacy)
 * into the target container at runtime. We render a mount-point `<div>` and
 * inject the `<script>` tag inside it.
 *
 * Deduplicated per `jsFormId` via a module-level Set. Lazy by default.
 */

import { useEffect, useId, useRef, useState } from 'react';

const injectedFormIds = new Set<string>();

interface JotFormScriptProps {
  jsFormId: string;
  title: string;
  lazy?: boolean;
  className?: string;
}

export function JotFormScript({ jsFormId, title, lazy = true, className }: JotFormScriptProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const targetId = `jotform-mount-${useId().replace(/:/g, '')}`;
  const [visible, setVisible] = useState(!lazy);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!lazy || !containerRef.current) return;
    const el = containerRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [lazy]);

  useEffect(() => {
    if (!visible || !containerRef.current) return;
    const dedupeKey = `${jsFormId}::${targetId}`;
    if (injectedFormIds.has(dedupeKey)) return;
    injectedFormIds.add(dedupeKey);

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = `https://form.jotform.com/jsform/${jsFormId}`;
    script.async = true;
    script.onerror = () => setError(true);
    containerRef.current.appendChild(script);

    return () => {
      injectedFormIds.delete(dedupeKey);
    };
  }, [visible, jsFormId, targetId]);

  if (error) {
    return (
      <div className={className} style={{ padding: '1rem', textAlign: 'center', color: '#666' }}>
        Unable to load form. Please refresh the page or{' '}
        <a href="tel:828-483-4237" style={{ color: '#ED8326' }}>
          call us at 828-483-4237
        </a>
        .
      </div>
    );
  }

  return <div ref={containerRef} id={targetId} className={className} role="region" aria-label={title} />;
}
